import { NavigateFunction } from 'react-router-dom'
import { message } from 'antd'

import { CHECKOUT_PATH, QUOTES_PATH } from '~/redux-modules/preferences'
import { getUserIdentity } from '~/redux-modules/identity'
import logger from '~/lib/logger'
import getErrorMessage from '~/lib/getErrorMessage'

export const FAILED_ACCEPT_QUOTE_INVITE = 'Failed to accept quote invite.'
export const ACCEPT_INVITE_QUERY_STRING = 'acceptInvite=true'
export const DEFAULT_ERROR_MESSAGE_TIME = 10

//update value to quote_invite_token when graphql supports
const QUOTE_TOKEN = 'project_invite_token='
const ACCEPT_OPEN_INVITE = 'accept-open-invite'
const TOKEN_LENGTH = 32

interface InviteTokensType {
    quoteInviteToken: string | null
    quoteOpenInviteToken: string | null
}

const extractTokenFromPath = (path: string, tokenType: string) => {
    const tokenTypeIndex = path.indexOf(tokenType)
    const pathContainsTokenType = tokenTypeIndex !== -1

    if (pathContainsTokenType) {
        const tokenIndex = tokenTypeIndex + tokenType.length
        const tokenLength = tokenIndex + TOKEN_LENGTH
        const token = path.slice(tokenIndex, tokenLength)

        return token
    }

    return null
}

export const getInviteTokens = (path: string): InviteTokensType => {
    const inviteTokens: InviteTokensType = {
        quoteInviteToken: null,
        quoteOpenInviteToken: null,
    }

    const quoteToken = extractTokenFromPath(path, QUOTE_TOKEN)

    if (quoteToken) {
        const pathContainsAcceptOpenInvite =
            path.indexOf(ACCEPT_OPEN_INVITE) !== -1

        if (pathContainsAcceptOpenInvite) {
            inviteTokens.quoteOpenInviteToken = quoteToken
        } else {
            inviteTokens.quoteInviteToken = quoteToken
        }
    }

    return inviteTokens
}

export const selectUserId = (state: { identity: { id: number } }) =>
    getUserIdentity(state).id

export const FORWARD_TO_PURCHASER_PARAMS = 'forward_to_purchaser=true'

const checkShouldRedirectToCheckout = () =>
    (window.location.search || '').includes(FORWARD_TO_PURCHASER_PARAMS)

export const acceptInvite = async ({
    id,
    inviteTokens,
    acceptQuoteOpenInvite,
    acceptQuoteInvite,
    navigate,
}: {
    id: string
    inviteTokens: InviteTokensType
    acceptQuoteOpenInvite: Function
    acceptQuoteInvite: Function
    navigate: NavigateFunction
}) => {
    const { quoteOpenInviteToken, quoteInviteToken } = inviteTokens

    const shouldRedirectToCheckout = checkShouldRedirectToCheckout()

    const redirectQuotePath = shouldRedirectToCheckout
        ? CHECKOUT_PATH
        : QUOTES_PATH
    const redirectUrl = `${redirectQuotePath}/${
        id || ''
    }?${ACCEPT_INVITE_QUERY_STRING}`

    if (quoteOpenInviteToken) {
        try {
            const { data } = await acceptQuoteOpenInvite({
                variables: { token: quoteOpenInviteToken },
            })

            if (data?.acceptOpenInvite?.id) {
                await navigate(redirectUrl)
            }
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: 'AcceptInvitePage::Failed to accept quote open invite',
                    quoteOpenInviteToken,
                },
            })
            message.error(
                getErrorMessage(err) || FAILED_ACCEPT_QUOTE_INVITE,
                DEFAULT_ERROR_MESSAGE_TIME,
            ) // default showing message is 3s, to short to notice for customer
            await navigate(QUOTES_PATH)
        }
    }

    if (quoteInviteToken) {
        try {
            const { data, errors } = await acceptQuoteInvite({
                variables: { token: quoteInviteToken },
            })

            if (data?.acceptInvite?.id) {
                await navigate(redirectUrl)
            } else if (errors) {
                throw errors
            }
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: 'AcceptInvitePage::Failed to accept quote email invite',
                    quoteInviteToken,
                },
            })
            message.error(
                getErrorMessage(err) || FAILED_ACCEPT_QUOTE_INVITE,
                DEFAULT_ERROR_MESSAGE_TIME,
            ) // default showing message is 3s, to short to notice for customer
            navigate(QUOTES_PATH)
        }
    }
}
